import { useEffect, useState } from 'react'
import { MobileContentGems } from '@mc/api'

type UseStickyCardsStackProps = {
  mobileContentGems: MobileContentGems[]
  cardRefs: React.MutableRefObject<(HTMLDivElement | null)[]>
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  overlayClass: string
  stickyTitleClass: string
}

export const useStickyCardsStack = ({
  mobileContentGems,
  cardRefs,
  containerRef,
  overlayClass,
  stickyTitleClass,
}: UseStickyCardsStackProps) => {
  const [isStacked, setIsStacked] = useState(false)

  useEffect(() => {
    if (!mobileContentGems?.length || isStacked) return

    const handleScroll = () => {
      cardRefs.current.forEach((cardRef, index) => {
        if (!cardRef) return

        const rect = cardRef.getBoundingClientRect()
        const expectedStickyTop = (index + 1) * 60
        const isSticky = rect.top <= expectedStickyTop + 2

        let animationProgress = 0

        if (isSticky && index < cardRefs.current.length - 1) {
          const nextCardRef = cardRefs.current[index + 1]
          if (nextCardRef) {
            const nextRect = nextCardRef.getBoundingClientRect()
            const nextExpectedTop = (index + 2) * 60
            const distanceOfNextCard = nextRect.top - nextExpectedTop
            if (distanceOfNextCard <= 15) {
              animationProgress = Math.max(
                0,
                Math.min(1, (15 - distanceOfNextCard) / 15),
              )
            }
          }
        }

        const overlayEl = cardRef.querySelector(
          `.${overlayClass}`,
        ) as HTMLElement | null
        const titleCaptionEl = cardRef.querySelector(
          `.${stickyTitleClass}`,
        ) as HTMLElement | null
        const titleTextEl = titleCaptionEl
          ? (titleCaptionEl.querySelector('span') as HTMLElement | null)
          : null

        if (overlayEl) {
          overlayEl.style.opacity = String(animationProgress * 0.65)
        }

        if (titleTextEl) {
          titleTextEl.style.opacity = String(animationProgress)
          const y = 20 * (1 - animationProgress)
          titleTextEl.style.transform = `translateY(${y}px)`
        }
      })

      const lastIndex = cardRefs.current.length - 1
      const lastCardRef = cardRefs.current[lastIndex]

      if (lastCardRef) {
        const lastRect = lastCardRef.getBoundingClientRect()
        const lastExpectedTop = (lastIndex + 1) * 60
        const lastIsSticky = lastRect.top <= lastExpectedTop + 2

        if (lastIsSticky) {
          const originalHeight = containerRef.current?.scrollHeight || 0
          const currentScrollY = window.scrollY
          const containerTop =
            containerRef.current?.getBoundingClientRect().top || 0
          const containerOffsetTop = currentScrollY + containerTop

          setIsStacked(true)

          requestAnimationFrame(() => {
            const newHeight = containerRef.current?.scrollHeight || 0
            const heightDifference = originalHeight - newHeight

            if (currentScrollY > containerOffsetTop && heightDifference > 0) {
              const newScrollY = currentScrollY - heightDifference
              window.scrollTo({
                top: Math.max(0, newScrollY),
                behavior: 'instant',
              })
            }
          })
        }
      }
    }

    handleScroll()

    let ticking = false
    const smoothHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', smoothHandleScroll)

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', smoothHandleScroll)
    }
  }, [
    mobileContentGems,
    isStacked,
    cardRefs,
    containerRef,
    overlayClass,
    stickyTitleClass,
  ])

  return { isStacked }
}
